import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  InlineLoading,
  InlineNotification,
  Tile,
} from "@carbon/react";
import type { ImportPreview } from "../../types";

type Props = {
  preview: ImportPreview | null;
  skipAlreadyImported: boolean;
  onSkipAlreadyImportedChange: (skip: boolean) => void;
  busy: boolean;
  isConfirming: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * The foot of the review screen: whether lines imported before go in again, and the button that
 * finally creates something.
 */
const ConfirmImportPanel = ({
  preview,
  skipAlreadyImported,
  onSkipAlreadyImportedChange,
  busy,
  isConfirming,
  onCancel,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const alreadyImported = preview?.historyDuplicates.length ?? 0;

  return (
    <Tile className="pr-import__panel">
      {alreadyImported > 0 && (
        <Checkbox
          id="pr-import-skip-already-imported"
          labelText={t("purchaseRequests.import.skipAlreadyImported", {
            count: alreadyImported,
          })}
          checked={skipAlreadyImported}
          onChange={(_event, { checked }) =>
            onSkipAlreadyImportedChange(checked)
          }
        />
      )}

      <div className="pr-import__actions">
        <Button kind="secondary" onClick={onCancel}>
          {t("cta.cancel")}
        </Button>
        {isConfirming ? (
          <InlineLoading description={t("purchaseRequests.import.importing")} />
        ) : (
          <Button onClick={onConfirm} disabled={!preview?.canConfirm || busy}>
            {t("purchaseRequests.import.confirm")}
          </Button>
        )}
      </div>

      {preview && !preview.canConfirm && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          className="pr-import__notification"
          title={t("purchaseRequests.import.cannotConfirm")}
        />
      )}
    </Tile>
  );
};

export default ConfirmImportPanel;
